import { DecimalPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { MenuButtonComponent, MenuButtonComponent as MenuButtonComponent_1 } from "@components/menu-button/menu-button.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, timer } from "rxjs";
import { take } from "rxjs/operators";

import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandControlBinder } from "~app/common/services/cqs/command-control-binder/command-control-binder";
import { CommandControlCollectionBinder } from "~app/common/services/cqs/command-control-binder/command-control-collection-binder";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { FordelaService } from "~app/common/services/fordela.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { KundorderradDTO } from "~app/features/forsaljning/kundorder/models/KundorderradDTO";
import { OrderService } from "~app/features/forsaljning/order/order.service";
import { SetLeveransradKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/SetLeveransradKvantitetCommand";
import { DisabledControlDirective } from "../../../../../common/directives/disabled-control/disabled-control.directive";
import { AlternativMottagningsplats } from "../../common/alternativMottagningsplats";
import { SetLeveransradKvantitetEnqueued } from "../../common/events";
import { KundorderService } from "../../kundorder.service";
import { IKundorderDetailKundorder, IKundorderDetailKundorderrad, ILagerplats } from "../../models/kundorder-detail-model";
import { KundorderradCommentComponent } from "../kundorderrad-comment/kundorderrad-comment.component";
import { KundorderradValidation } from "../kundorderrad-create/kundorderrad.validation";
import { AddKundorderradAlternativaHandelssortimentCommand, Handelssortiment } from "./commands/AddKundorderradAlternativaHandelssortimentCommand";
import { AddKundorderradAlternativaMottagningsplatserCommand } from "./commands/AddKundorderradAlternativaMottagningsplatserCommand";
import { AddKundorderradCommand } from "./commands/AddKundorderradCommand";
import { AddKundorderradLagerplatserCommand } from "./commands/AddKundorderradLagerplatserCommand";
import { DeleteKundorderradCommand } from "./commands/DeleteKundorderradCommand";
import { EditKundorderradCommand } from "./commands/EditKundorderradCommand";
import { RemoveKundorderradAlternativaHandelssortimentCommand } from "./commands/RemoveKundorderradAlternativaHandelssortimentCommand";
import { RemoveKundorderradAlternativaMottagningsplatserCommand } from "./commands/RemoveKundorderradAlternativaMottagningsplatserCommand";
import { RemoveKundorderradLagerplatserCommand } from "./commands/RemoveKundorderradLagerplatserCommand";
import { KundorderradHandelssortimentComponent } from "./kundorderrad-handelssortiment/kundorderrad-handelssortiment.component";
import { KundorderradMottagningsplatserComponent } from "./kundorderrad-mottagningsplatser/kundorderrad-mottagningsplatser.component";

@Component({
    selector: "rask-kundorderrad-edit",
    templateUrl: "./kundorderrad-edit.component.html",
    styleUrls: ["./kundorderrad-edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        KundorderradMottagningsplatserComponent,
        NgClass,
        MultiSelectorComponent,
        DisabledControlDirective,
        KundorderradHandelssortimentComponent,
        InputNumberComponent,
        DatepickerComponent,
        InputComponent,
        CheckboxComponent,
        MenuButtonComponent_1,
        IconButtonComponent,
        NgbTooltip,
        ActionButtonComponent,
        DecimalPipe,
        TranslatePipe
    ]
})
export class KundorderradEditComponent implements OnInit {
    balans = 0;
    dateSpanChanged = false;
    @Input() enableCommands = true;
    form: UntypedFormGroup;
    @Input() isCopy = false;
    @Input() isCreate = false;
    isNew = false;
    @Input() kundorder: IKundorderDetailKundorder;
    @Input() kundorderTyp: string;
    lagerplatser: BehaviorSubject<ILagerplats[]> = new BehaviorSubject([]);
    @Input() mattslag: string;
    @ViewChild("menu") menuBtn: MenuButtonComponent;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("kundorderrad") model: IKundorderDetailKundorderrad;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onRemove = new EventEmitter();
    @Input() rootForm: UntypedFormGroup;
    @Input() showLabels = true;
    @Input() title: string;
    @Input() twoRowMode = false;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly commandService = inject(CommandService);
    private readonly eventService = inject(EventService);
    private readonly fordelaService = inject(FordelaService);
    private readonly formBinder = inject(FormBinderService);
    private readonly kundOrderService = inject(KundorderService);
    private readonly modalService = inject(RaskModalService);
    private readonly orderService = inject(OrderService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly translateService = inject(TranslateService);

    canEdit = () => this.authenticationService.canEdit();

    clearLagerplatser(item) {
        if (ObjectUtils.isNullOrUndefined(item)) {
            this.lagerplatser = null;
        }
    }

    delete() {
        this.menuBtn.toggle();
        const title = this.translateService.instant("KUNDORDER_DETAIL_KUNDORDERRAD_DELETE_PROMPT_HEADER");
        const settings = new ModalSettings(title, this.getDeletePromtMessage());
        settings.contentIsMarkup = true;
        this.modalService.confirm(settings).subscribe(r => {
            if (r === ConfirmDialogResponse.save) {
                this.commandService.addTransactionCommand(new DeleteKundorderradCommand(this.model.id, this.kundorder.id));
                this.onRemove.emit(this.model);
            }
        });
    }

    // Kallas från KundorderradCommentComponent enbart för att göra så att ikonen kan tändas/släckas om det har ändrats
    editComment = (newComment: string): void => {
        this.model.kommentar = newComment;
    };

    fordelaLeveransrad() {
        this.menuBtn.toggle();
        this.fordelaService
            .getFordeladKvantitet(this.model.kvantitet, this.model.periodFrom, this.model.periodTom)
            .subscribe(res => {
                const command = new SetLeveransradKvantitetCommand(c => {
                    c.kundorderradId = this.model.id;
                    c.periodStart = this.model.periodFrom;
                    c.periodEnd = this.model.periodTom;
                    c.fordeladKvantitet = res;
                });

                this.commandService.addTransactionCommand(command);
                this.eventService.emit(new SetLeveransradKvantitetEnqueued(command));
            });
    }

    getClass(size?: number, alternativeSize?: number, classToAdd?: string) {
        const result = [];
        if (size) {
            if (alternativeSize === undefined) {
                alternativeSize = size + 1;
            }
            result.push(`col-${this.twoRowMode ? alternativeSize : size}`);
        }

        if (!this.showLabels) {
            result.push("low");
        }

        if (this.isCreate && ObjectUtils.hasValue(classToAdd)) {
            result.push(classToAdd);
        }

        return result;
    }

    getDeletePromtMessage() {
        const text = this.translateService.instant("KUNDORDER_DETAIL_KUNDORDERRAD_DELETE_PROMPT_TEXT");

        const lagerplatserMessage = this.model.antalLagertransaktioner > 0
            ? this.translateService.instant("KUNDORDER_DETAIL_DELETE_CONFIRM_ROW_LAGERTRANSAKTIONER_EXISTS", { antalLagertransaktioner: this.model.antalLagertransaktioner })
            : "";
        const period = `${new Date(this.model.periodFrom).toLocaleDateString()} - ${new Date(this.model.periodTom).toLocaleDateString()}`;

        return `${text + period + lagerplatserMessage}`;
    }

    lagerplatsDisabled = (l) => !l.aktiv;

    lagerplatserViewFormatter = (l) => `${l.kod ?? l.avlamningsplats} ${l.namn}`;

    lagerplatserViewSelectedFormatter = (l) => `${l.kod ?? l.avlamningsplats}`;

    ngOnInit(): void {
        this.isNew = this.model.id === 0;
        this.updateLagerplatser(false);
        this.setUpForm();

        if (this.enableCommands) {
            this.bindControlsToCommands();
        }
        this.updateBalans();
    }

    updateBalans() {
        this.balans = this.kundOrderService.getBalans(this.model.leveransrader, this.model.kvantitet);
    }

    updateLagerplatser(clear = true) {
        if (!this.model || !this.model.mottagningsplats) {
            return;
        }
        if (this.model.mottagningsplats.lagerplatser !== this.lagerplatser.value) {
            this.orderService.getLagerplatser(this.model.mottagningsplats).subscribe(l => {
                this.lagerplatser.next(l);
                if (clear) {
                    this.form.controls.lagerplatser.setValue(null);
                }
            });
        }
    }

    onFormChanges = (v: KundorderradDTO) => {
        this.updateLagerplatser();
        this.dateSpanChanged = this.form.controls.periodTom.dirty || this.form.controls.periodFrom.dirty;
        this.updateBalans();
        setTimeout(() => {
            this.formBinder.updateErrors(this.validation, this.form);
        }, 200);
    };

    openComment(): void {
        this.raskModalService.open("Editera kommentar", KundorderradCommentComponent, "md", true, {
            kundorderradId: this.model.id,
            commentUpdatedCallback: this.editComment,
            editMode: true
        });
    }

    setUpForm() {
        this.form = this.formBinder.CreateGroup(this.model);
        this.form.valueChanges.subscribe(this.onFormChanges);
        this.validationContext = KundorderradValidation.init(this.translateService, this.model);
        this.validationContext.scope(ValidationScopes.skapa).subscribe(v => {
            this.validation = v;
        });
        this.formBinder.attachToRootForm(this.rootForm, this.form, `kundorder${this.model.id}`).
            pipe(take(1))
            .subscribe(x => {
                this.formBinder.attachValidation(this.validationContext.current, this.form, true);
                timer(0).subscribe(() => {
                    this.rootForm.updateValueAndValidity({
                        onlySelf: false,
                        emitEvent: true
                    });
                });
            });
    }

    updateLeveransrader() {
        this.menuBtn.toggle();
        this.kundOrderService.getLeveransraderForPeroid(this.model.leveransrader, this.model.periodFrom, this.model.periodTom)
            .subscribe(r => {
                this.model.leveransrader = r.leveransrader;
                this.dateSpanChanged = false;
                this.updateBalans();
            });
    }

    private bindAltHandelssortiment(items: any[]) {
        const res: Handelssortiment[] = [];
        items.forEach(x => {
            const obj = {
                id: x.id,
                handelssortimentId: x.handelssortimentId,
                manuellPaArbetsorder: x.manuellPaAo,
                clientIdGuid: x.clientIdGuid,
                endastCentralaKop: x.endastCentralaKop,
                inmatningshandelssortimentId: x.inmatningshandelssortimentId,
                egenskapskodX: x.egenskapskodX
            } as Handelssortiment;
            res.push(obj);
        });

        return res;
    }

    private bindAltHandelssortimentRemove(items: any[]) {
        return items.map(x => x.id);
    }

    private bindControlsToCommands() {
        const ctrls = this.form.controls;

        if (!this.isNew) {
            const lagerplatserBinder = new CommandControlCollectionBinder(this.model.id, this.commandService, ctrls.lagerplatser);
            lagerplatserBinder.bindAdd(AddKundorderradLagerplatserCommand, (v, c) => c.lagerplatser = v.map(vm => vm.id));
            lagerplatserBinder.bindRemove(RemoveKundorderradLagerplatserCommand, (v, c) => c.lagerplatser = v.map(vm => vm.id));
        }

        let kundorderradBinder: CommandControlBinder;
        if (this.isNew) {
            kundorderradBinder = new CommandControlBinder(this.kundorder.id, this.commandService, AddKundorderradCommand);
            kundorderradBinder.bindControl(ctrls.lagerplatser, (v, c) => c.lagerplatser = v?.map(vm => vm.id));
        } else {
            kundorderradBinder = new CommandControlBinder(this.model.id, this.commandService, EditKundorderradCommand);
        }

        kundorderradBinder.bindControl(ctrls.periodFrom, "periodFrom");
        kundorderradBinder.bindControl(ctrls.periodTom, "periodTom");
        kundorderradBinder.bindControl(ctrls.manuellPaArbetsorder, "manuellPaArbetsorder");
        kundorderradBinder.bindControl(ctrls.inmatningshandelssortiment, (v, c) => c.inmatningshandelssortimentId = v?.id, true);
        kundorderradBinder.bindControl(ctrls.egenskapskodX, "egenskapskodX");
        kundorderradBinder.bindControl(ctrls.ejVirkeskop, "ejVirkeskop");
        kundorderradBinder.bindControl(ctrls.mottagningsplats, (v, c) => c.mottagningsplatsId = v?.id);
        kundorderradBinder.bindControl(ctrls.handelssortiment, (v, c) => c.handelssortimentId = v?.id);
        kundorderradBinder.bindControl(ctrls.kvantitet, "kvantitet");

        const altMottagningsplatserBinder = new CommandControlCollectionBinder(this.model.id, this.commandService, ctrls.alternativaMottagningsplatser);
        const altMottagningsplatsBindExpression = (v, c) => c.alternativaMottagningsplatser = v.map(i =>
            new AlternativMottagningsplats(i.mottagningsplatsId, i.lagerplatsId)
        );
        altMottagningsplatserBinder.bindAdd(AddKundorderradAlternativaMottagningsplatserCommand, altMottagningsplatsBindExpression);
        altMottagningsplatserBinder.bindRemove(RemoveKundorderradAlternativaMottagningsplatserCommand, altMottagningsplatsBindExpression);

        const altHandelssortimentBinder = new CommandControlCollectionBinder(this.model.id, this.commandService, ctrls.alternativaHandelssortiment);

        const altHandelssortimentBindExpression = (v, c) => c.handelssortiment = this.bindAltHandelssortiment(v);
        const altHandelssortimentBindRemoveExpression = (v, c) => c.handelssortiment = this.bindAltHandelssortimentRemove(v);
        altHandelssortimentBinder.bindAdd(AddKundorderradAlternativaHandelssortimentCommand, altHandelssortimentBindExpression);
        altHandelssortimentBinder.bindRemove(RemoveKundorderradAlternativaHandelssortimentCommand, altHandelssortimentBindRemoveExpression);

        if (this.isNew) {
            kundorderradBinder.manualPush();
        }
    }
}
